import { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import "../LayoutListPage/LayoutListPage.scss";
import SideNavbar from "../../components/Navbar/SideNavbar";
import Header from "../../components/Header/Header";
import { getReviewsDump, uploadReviewsFile, generatePresignedUrl } from "../../../core/actionCreators/rmActionCreator";
import { uploadImageService } from "../../../core/services/utilService";
import { downloadFile } from "../../../core/services/utilService";
import toast from "react-hot-toast";
import ReviewList from "../../components/ReviewModeraton/ReviewList";


interface MediaFile {
    file: File;
    id: string;
    status: "pending" | "uploading" | "success" | "error";
    url?: string;
    error?: string;
}

const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Reviews = () => {
    const dispatch = useDispatch();
    let { user } = useSelector((s: any) => s.authStore);

    const [file, setFile] = useState("");

    const [showMediaModal, setShowMediaModal] = useState(false);
    const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const mediaInputRef = useRef<HTMLInputElement>(null);

    const downloadDump = () => {
        dispatch(getReviewsDump(user['email'] || 'anil.payasia@moglix.com', (reviewsDump: any) => {
            downloadFile(reviewsDump, 'reviewDump');
        }))
    }

    const fileHandler = (e: any) => {
        if (e.target.files && e.target.files[0]) {
            let file = e.target.files[0];
            setFile(file)
        }
    }

    const bulkUploadHandler = () => {
        if (!file) {
            toast.error("Please select file.")
            return
        }
        const formData = new FormData()
        formData.append("file", file)
        dispatch(uploadReviewsFile({
            param: `email=${user.email || "anil.payasia@moglix.com"}`,
            data: formData
        }, (d: any) => {
            toast.success("Uploaded successfully.")
        }))
    }

    // ── media upload handlers ────────────────────────────────────────────────

    const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const newFiles: MediaFile[] = Array.from(e.target.files).map((f) => ({
            file: f,
            id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
            status: "pending",
        }));
        setMediaFiles((prev) => [...prev, ...newFiles]);
        if (mediaInputRef.current) mediaInputRef.current.value = "";
    };

    const removeMediaFile = (id: string) => {
        setMediaFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const handleUploadAll = async () => {
        const pending = mediaFiles.filter((f) => f.status === "pending");
        if (pending.length === 0) { toast.error("No pending files."); return; }
        setIsUploading(true);
        setMediaFiles((prev) => prev.map((f) => f.status === "pending" ? { ...f, status: "uploading" as const } : f));

        await Promise.all(pending.map(async (mf) => {
            try {
                const isVideo = mf.file.type.startsWith("video/");
                let url = "";
                if (isVideo) {
                    // Videos are uploaded directly to S3 via a presigned URL
                    // (the multipart uploadImage endpoint is not suited for large files).
                    url = await new Promise<string>((resolve, reject) => {
                        dispatch(generatePresignedUrl(
                            {
                                fileType: "video",
                                fileName: mf.file.name,
                                contentType: mf.file.type || "application/octet-stream",
                                contentLength: mf.file.size,
                                file: mf.file,
                            },
                            (err: any, result: any) => {
                                if (err) reject(err);
                                else resolve(result?.fileUrl || "");
                            }
                        ));
                    });
                } else {
                    const formData = new FormData();
                    formData.append("file", mf.file);
                    const { data } = await uploadImageService(formData);
                    url = data?.data?.s3ImageUrl || "";
                }
                setMediaFiles((prev) => prev.map((f) => f.id === mf.id ? { ...f, status: "success", url } : f));
            } catch (err: any) {
                setMediaFiles((prev) => prev.map((f) => f.id === mf.id ? { ...f, status: "error", error: err?.message || "Upload failed" } : f));
            }
        }));

        setIsUploading(false);
        toast.success("Done!");
    };

    const copyUrl = async (id: string, url: string) => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            const ta = document.createElement("textarea");
            ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
        }
        setCopiedId(id);
        toast.success("URL copied!");
        setTimeout(() => setCopiedId(null), 2000);
    };

    const closeModal = () => {
        setShowMediaModal(false);
        setMediaFiles([]);
    };



    return (
        <>
            {/* Spin keyframe */}
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            <div className="flex mainWrapper w-full">
                <div className="sidenav-wrapper">
                    <SideNavbar />
                </div>
                <div className="right-container">
                    <div className="header-container">
                        <Header />
                    </div>
                    <div className="body-container" >
                        <div className="p-4">
                            <div className="flex items-center justify-between w-full">
                                <p className="text-xl font-bold text-[#43425D]">REVIEWS &amp; RATINGS</p>
                                <div className="flex items-center gap-4">
                                    <input type="file" onChange={(e) => fileHandler(e)} className="border border-gray-300 py-2 rounded focus:outline-none px-1.5 text-xs w-[220px]" />
                                    <button onClick={bulkUploadHandler} className="h-9 py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75">Bulk Upload</button>
                                    <button className="h-9 py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75" onClick={() => downloadDump()}>Download Dump</button>

                                    {/* ── Upload Media CTA ── */}
                                    <button
                                        id="upload-media-presigned-url-btn"
                                        onClick={() => setShowMediaModal(true)}
                                        className="h-9 py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 whitespace-nowrap"
                                    >
                                        Upload Media
                                    </button>
                                </div>
                            </div>
                        </div>
                        <ReviewList />
                    </div>
                </div>
            </div>

            {/* ────────────────── Upload Media Modal ────────────────────── */}
            {showMediaModal && (
                <div
                    style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,15,30,0.55)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
                >
                    <div style={{ background: "#fff", borderRadius: 16, width: "min(680px, 95vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.18)", overflow: "hidden" }}>
                        {/* Header */}
                        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f0f0f4", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                                <p style={{ fontSize: 17, fontWeight: 700, color: "#1e1b4b", margin: 0 }}>Upload Media</p>
                                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>Select images or videos — upload and copy the URL.</p>
                            </div>
                            <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 4 }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        {/* Drop zone */}
                        <div style={{ padding: "16px 24px" }}>
                            <label htmlFor="media-upload-input"
                                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "2px dashed #c4b5fd", borderRadius: 12, padding: "24px 16px", cursor: "pointer", background: "#faf5ff", gap: 6 }}
                                onMouseOver={(e) => (e.currentTarget.style.background = "#f3e8ff")}
                                onMouseOut={(e) => (e.currentTarget.style.background = "#faf5ff")}
                            >
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                                </svg>
                                <span style={{ fontSize: 13, fontWeight: 600, color: "#6d28d9" }}>Click to select images or videos</span>
                                <span style={{ fontSize: 11, color: "#9ca3af" }}>JPG, PNG, GIF, WEBP, MP4, MOV and more</span>
                                <input id="media-upload-input" ref={mediaInputRef} type="file" multiple accept="image/*,video/*" style={{ display: "none" }} onChange={handleMediaSelect} />
                            </label>
                        </div>

                        {/* File list */}
                        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 8px" }}>
                            {mediaFiles.length === 0 ? (
                                <p style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: "12px 0" }}>No files selected yet.</p>
                            ) : (
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ color: "#6b7280", borderBottom: "1px solid #f0f0f4" }}>
                                            <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>File</th>
                                            <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600 }}>Size</th>
                                            <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600 }}>Status</th>
                                            <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>URL</th>
                                            <th style={{ padding: "6px 4px" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mediaFiles.map((mf) => {
                                            const statusColor = mf.status === "success" ? "#10b981" : mf.status === "error" ? "#ef4444" : mf.status === "uploading" ? "#f59e0b" : "#6b7280";
                                            const statusLabel = mf.status === "uploading" ? "Uploading…" : mf.status === "success" ? "Done" : mf.status === "error" ? "Error" : "Pending";
                                            return (
                                                <tr key={mf.id} style={{ borderBottom: "1px solid #f9f9fb" }}>
                                                    <td style={{ padding: "8px", color: "#374151", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={mf.file.name}>{mf.file.name}</td>
                                                    <td style={{ padding: "8px", textAlign: "center", color: "#6b7280" }}>{formatBytes(mf.file.size)}</td>
                                                    <td style={{ padding: "8px", textAlign: "center" }}>
                                                        <span style={{ background: statusColor + "20", color: statusColor, border: `1px solid ${statusColor}40`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                                                            {mf.status === "uploading" ? (
                                                                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: "spin 1s linear infinite" }}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
                                                                    {statusLabel}
                                                                </span>
                                                            ) : statusLabel}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: "8px", maxWidth: 200 }}>
                                                        {mf.status === "success" && mf.url ? (
                                                            <span style={{ color: "#6366f1", fontSize: 11, wordBreak: "break-all", display: "block", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={mf.url}>{mf.url}</span>
                                                        ) : mf.status === "error" ? (
                                                            <span style={{ color: "#ef4444", fontSize: 11 }}>{mf.error}</span>
                                                        ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>}
                                                    </td>
                                                    <td style={{ padding: "8px", textAlign: "center", whiteSpace: "nowrap" }}>
                                                        {mf.status === "success" && mf.url && (
                                                            <button
                                                                onClick={() => copyUrl(mf.id, mf.url!)}
                                                                title="Copy URL"
                                                                style={{ background: copiedId === mf.id ? "#d1fae5" : "#f3f4f6", border: "none", borderRadius: 6, cursor: "pointer", padding: "4px 8px", color: copiedId === mf.id ? "#059669" : "#6b7280", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, transition: "all .15s" }}
                                                            >
                                                                {copiedId === mf.id
                                                                    ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>Copied</>                                                                    : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>Copy</>}
                                                            </button>
                                                        )}
                                                        {mf.status !== "uploading" && (
                                                            <button onClick={() => removeMediaFile(mf.id)} title="Remove" style={{ background: "none", border: "none", cursor: "pointer", color: "#d1d5db", padding: "4px", marginLeft: 2 }}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f4", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                            <span style={{ fontSize: 12, color: "#6b7280" }}>
                                {mediaFiles.length} file{mediaFiles.length !== 1 ? "s" : ""}
                                {mediaFiles.filter(f => f.status === "success").length > 0 && ` · ${mediaFiles.filter(f => f.status === "success").length} uploaded`}
                            </span>
                            <div style={{ display: "flex", gap: 10 }}>
                                <button onClick={closeModal} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
                                <button
                                    id="upload-media-btn"
                                    onClick={handleUploadAll}
                                    disabled={isUploading || mediaFiles.filter(f => f.status === "pending").length === 0}
                                    style={{ padding: "8px 22px", borderRadius: 8, border: "none", background: isUploading || mediaFiles.filter(f => f.status === "pending").length === 0 ? "#c4b5fd" : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: isUploading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7, boxShadow: "0 2px 8px rgba(99,102,241,0.25)" }}
                                >
                                    {isUploading ? (
                                        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: "spin 1s linear infinite" }}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>Uploading…</>
                                    ) : (
                                        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>Upload</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Reviews;

